export type Settled<T> =
  | { ok: true; value: T }
  | { ok: false; error: unknown }

interface FailFastOption {
  failFast: true
}

interface AggregateOption {
  failFast: false
}

export function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]>
export function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
  options: FailFastOption,
): Promise<R[]>
export function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
  options: AggregateOption,
): Promise<Settled<R>[]>
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
  options: { failFast?: boolean } = {},
): Promise<R[] | Settled<R>[]> {
  if (items.length === 0) return [] as R[]
  const failFast = options.failFast !== false
  const results: (R | Settled<R>)[] = new Array(items.length)
  let cursor = 0
  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      while (true) {
        const i = cursor++
        if (i >= items.length) return
        try {
          const value = await task(items[i], i)
          results[i] = failFast ? value : { ok: true, value }
        } catch (error) {
          if (failFast) throw error
          results[i] = { ok: false, error }
        }
      }
    },
  )
  await Promise.all(workers)
  return results as R[] | Settled<R>[]
}
